import React, {useState} from 'react';
import {Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import PartnerDashboardShell from '../components/PartnerDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import {NAVY, TEAL, PageHeader, headCell, bodyCell, BandChip} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, rowMatches} from '../../DashboardCommon/dashboardCards';
import {RowCell} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

const ORANGE = '#FF6900';

/* Slides 25-26 — Learning Impact Tracker (By Program) and by COS Area.
   Live data (Data Map PD-25-02, PD-26-01):
     By Program   PD.S_21.C_2   ·   By COS Area   PD.S_21.C_3
   Columns follow the mockup (PD slide 50): the partner spans countries, so
   Country and Region lead. A column the card doesn't send reads "—". Every
   column sorts and filters from its header (QA 9 Oct 2026 — the old drop-down
   filter bar is gone); the search box above the By Program table matches any
   cell. Same layout as the Network dashboard's live Learning Impact page. */
const signalColor = (signal) => {
    if (signal === 'Strong') return TEAL;
    if (signal === 'Moderate') return '#EFA020';
    return '#6A7282';
};
const signalChip = (signal) => (!signal || signal === '—' ? '—' : <BandChip label={signal} color={signalColor(signal)}/>);
const emptyRow = (cols, text) => (
    <TableRow>
        <TableCell colSpan={cols} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{text}</TableCell>
    </TableRow>
);
const tableBox = {
    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};
const rowBg = (i) => ({backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'});

const SIGNAL_ORDER = ['Strong', 'Moderate', 'Early', 'Weak'];
const PROGRAM_COLUMNS = [
    {key: 'country', label: 'Country', type: 'text', align: 'left'},
    {key: 'region', label: 'Region', type: 'text', align: 'left'},
    {key: 'school', label: 'School', type: 'text', align: 'left'},
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'start', label: 'Start Date', type: 'date'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impact', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const AREA_COLUMNS = [
    // "COS Area", as the mockup's slide 26 header reads.
    {key: 'area', label: 'COS Area', type: 'text', align: 'left'},
    {key: 'baseline', label: 'Baseline Score', type: 'number'},
    {key: 'countries', label: 'Countries Covered', type: 'number'},
    {key: 'schools', label: 'Schools Covered', type: 'number'},
    {key: 'students', label: 'Students Reached', type: 'number'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impact', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const headerCells = (t, columns) => columns.map((c, i) => (
    <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
));

// The tables sit in their own components so the sort/filter hook runs below the page's loading branches.
const ByProgramTable = ({rows, query, onQuery}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search by country, region, school…"/>
            <TableContainer sx={tableBox}>
                <Table sx={{minWidth: 1300}}>
                    <TableHead>
                        <TableRow>{headerCells(t, PROGRAM_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(13, rows.length ? 'No row matches the search and filters.' : 'No learning impact recorded yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.school}-${row.program}-${rows.indexOf(row)}`} sx={rowBg(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.country}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.region}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.start}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.rate}</TableCell>
                                {/* Score columns of a row under the n floor arrive flagged: placeholder, no chip. */}
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impact"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const ByCosAreaTable = ({rows}) => {
    const t = useTableControls(rows, AREA_COLUMNS);
    return (
        <>
            <TableToolbar {...t}/>
            <TableContainer sx={tableBox}>
                <Table sx={{minWidth: 1000}}>
                    <TableHead>
                        <TableRow>{headerCells(t, AREA_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(9, rows.length ? 'No row matches the filters.' : 'No learning impact recorded yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.area}-${rows.indexOf(row)}`} sx={rowBg(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.area}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="baseline"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.countries}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.schools}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.students}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impact"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const LEARNING_IMPACT_SECTION_CODE = 'S_21';
const CARD_SECTIONS = [LEARNING_IMPACT_SECTION_CODE];

const LearningImpactPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [query, setQuery] = useState('');
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    const s21Section = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === LEARNING_IMPACT_SECTION_CODE
    );

    const programRows = rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_2'), {
        country: 'Country', region: 'Region', school: 'School', program: 'Program', targetArea: 'Target Area',
        start: 'Start Date', enrolled: 'Students Enrolled', completed: 'Students Completed', rate: ['Completion Rate %', 'Completion Rate'],
        pre: ['Avg. Pre', 'Avg. Pre-Assessment'], post: ['Avg. Post', 'Avg. Post-Assessment'],
        impact: ['Avg. Impact Rate %', 'Avg. Impact Rate'], signal: 'Impact Signal',
    });
    const areaRows = rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_3'), {
        area: 'COS Area', baseline: 'Baseline Score', countries: ['Countries Covered', 'Countries'], schools: ['Schools Covered', 'Schools'],
        students: 'Students Reached', pre: ['Avg. Pre', 'Avg. Pre-Assessment'], post: ['Avg. Post', 'Avg. Post-Assessment'],
        impact: ['Avg. Impact Rate %', 'Avg. Impact Rate'], signal: 'Impact Signal',
    });
    const q = query.trim().toLowerCase();
    const shownPrograms = programRows.filter((r) => rowMatches(r, q));

    return (
        <PartnerDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 2,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="By Program"/>
                    <Tab label="By COS Area"/>
                </Tabs>
            }
        >
            <PageHeader
                title={activeTab === 0 ? 'Learning Impact' : 'Learning Impact Tracker by COS Area'}
                subtitle="Review learning improvement signals from deployed programs."
            />

            {loading ? (
                <CircularLoader />
            ) : !s21Section ? (
                <NoDataAvailable />
            ) : activeTab === 0 ? (
                <ByProgramTable rows={shownPrograms} query={query} onQuery={setQuery}/>
            ) : <ByCosAreaTable rows={areaRows}/>}
        </PartnerDashboardShell>
    );
};

export default LearningImpactPage;
